import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  Divider,
} from "@mui/material";
import { Helmet } from "react-helmet-async";

import HaveQuestions from "../components/HaveQuestions";

import aboutBg from "../assets/about-bg.png";
import diffBg from "../assets/Differentiation-bg.png";
import immrsExpBg from "../assets/Immersive-Experience-bg.png";
import servicesBg from "../assets/DAM-Service.png";
import productAgBg from "../assets/productAgnosticBg.png";
import DAMTrainingCourseBg from "../assets/DAMTrainingCourse-bg.png";
import discoveryBg from "../assets/damDiscovery.png";
import practiceBg from "../assets/practicePage.png";
import DAMJourneyBg from "../assets/DAMJourneyBg-PS.png";
import filterImage from "../assets/FilterVintageOutlined.png";
import { Countries } from "../constants/Countries";
import { States } from "../constants/States";

const randomPages = [
  { linkLabel: "About",
    text: "Helping organizations streamline, organize, and maximize the value of their digital assets through expert guidance and innovative solutions.", 
    image: aboutBg,
    url: "/about",
  },
  { linkLabel: "Differentiation",
    text: "We don’t just implement DAM—we transform how your organization works with digital assets through strategy, execution, and continuous optimization.", 
    image: diffBg,
    url: "/differentiation",
  },
  { linkLabel: "Immersive Experience",
    text: "We opt in for an immersive experience. This means that we work the details with practitioners in your organization in a real-life and real-time learning journey. We accomplish this by executing our proprietary DAM playbook with you and your team members as active contributors. This also means no webinars and certainly no certification classes.", 
    image: immrsExpBg,
    url: "/immersive-experience",
  },
  { linkLabel: "Services",
    text: "From strategy to implementation and beyond — we help you design, deploy, and optimize Digital Asset Management solutions that scale with your business.", 
    image: servicesBg,
    url: "/services",
  },
  { linkLabel: "Product Agnostic",
    text: "We don’t sell tools—we design the right DAM ecosystem tailored to your business goals.", 
    image: productAgBg,
    url: "/product-agnostic",
  },
  { linkLabel: "DAM Training",
    text: "Master the principles of Digital Asset Management through a structured, product-agnostic training program designed for real-world implementation.", 
    image: DAMTrainingCourseBg,
    url: "/dam-trainiong-corporate",
  },
  { linkLabel: "Discovery Framework",
    text: "A structured methodology designed to capture your current DAM processes, challenges, and requirements—laying the foundation for the right solution.", 
    image: discoveryBg,
    url: "/discovery-framework",
  },
  { linkLabel: "Our Practice",
    text: "Inspired by the tradition of professional practices in medicine and law, our firm is built on the principles of “The Practice.” We apply this same level of vocational dedication and specialized expertise to your digital assets.", 
    image: practiceBg,
    url: "/our-practice",
  },
  { linkLabel: "DAM Journey",
    text: "We walk with you through your Asset Lifecycle Management journey", 
    image: DAMJourneyBg,
    url: "/dam-journey",
  },
];

const Contact = () => {

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [{ linkLabel, text, image, url }] = useState(
    () => randomPages[Math.floor(Math.random() * randomPages.length)]
  );

  const [formData, setFormData] = useState({
    companyName: "",
    fullName: "",
    jobTitle: "",
    email: "",
    country: "",
    state: "",
    referral: "",
    message: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = "This field is required.";
    }

    if (!formData.fullName.trim()) {
      newErrors.fullName = "This field is required.";
    }

    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = "This field is required.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "This field is required.";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.country) {
      newErrors.country = "This field is required.";
    }

    if (!formData.state) {
      newErrors.state = "This field is required.";
    }

    if (!formData.referral.trim()) {
      newErrors.referral =
        "This field is required.";
    }

    if (!formData.message.trim()) {
      newErrors.message = 
        "This field is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    
    try {
      const response = await fetch(
        "https://damjourney.com/api/contact/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
          navigate("/congrats");
      } else {
          alert(result.message || "Failed to submit form.");
      }
    } catch (error) {
      console.error("Submit error:", error);
      alert("An error occurred while submitting the form.");
    }
  };


  return (
    <Box sx={{ backgroundColor: "#F9F9F9" }}>
      <Helmet>
        <title>DAM Journey - Contact</title>
        <meta
          name="description"
          content="Helping your organization to discover, plan, implement, and operate Digital Asset Management solutions to unlock the full value of your assets."
        />
        <meta
          property="keywords"
          content="DAM, DAM Journey, DAM Practice, DAM Consultant, DAM Consulting, DAM Practitioner, DAM Service, DAM Product, DAM Discovery, DAM Implementation, DAM Deployment, Contact"
        />
      </Helmet>

      {/* Contact / Demo Request Section */}
      <Box
        sx={{
          py: "48px",
          px: "84px",
          "@media (max-width: 1200px)": {
            px: "24px",
          },
          "@media (max-width: 780px)": {
            px: "16px",
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: "#F5F5F5",
            borderRadius: "16px",
            overflow: "hidden",
            display: "flex",
            minHeight: "640px",
            "@media (max-width: 1200px)": {
              flexDirection: "column",
            },
          }}
        >
          {/* LEFT PANEL */}
          <Box
            sx={{
              width: "50%",
              background: "linear-gradient(97.94deg, rgba(255, 193, 215, 0.3) 5.07%, #DBDAFD 94.73%)",
              px: "32px",
              pt: "32px",
              pb: "70px",
              gap: "24px",
              display:"flex",
              flexDirection:"column",
              "@media (max-width: 1200px)": {
                width: "100%",
              },
              "@media (max-width: 780px)": {
                px: "16px",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: "regular",
                lineHeight: 1.8,
                color: "#666666",
              }}
            >
              Helping your organization to discover, plan,
              implement, and operate Digital Asset Management
              solutions to unlock the full value of your assets.
            </Typography>

            <Box
              sx={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",                
                py: "20px",
                gap: "10px",
              }}
            >
              <Divider 
                sx={{ 
                  width: "100%",
                  "@media (max-width: 1200px)": {
                    borderWidth: "1px",
                  },
                }}
              />

              <Box
                component="img"
                src={filterImage}
                alt=""
                sx={{
                  position: "absolute",
                  width: 45,
                  height: 45,
                  backgroundColor: "transparent",
                }}
              />
            </Box>
            
            <Box
              component="img"
              src={image}
              alt="DAM Journey Services"
              sx={{                
                width: "532px",
                display: "block",
                mx: "auto",
                gap: "10px",
                "@media (max-width: 1200px)": {
                  width: "656px",
                },
                "@media (max-width: 780px)": {
                  width: "296px",
                },
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "baseline",
                color: "#666666",
                fontSize: "18px",
                lineHeight: "150%",

                "@media (max-width: 780px)": {
                  flexDirection: "column",
                  alignItems: "flex-start",
                },
              }}
            >
              <Link
                href={url}
                sx={{
                  color: "#1E0E62",
                  fontWeight: 700,
                  textDecoration: "underline",
                  mb: {
                    xs: "4px",
                    sm: 0,
                  },
                }}
              >
                {linkLabel}:&nbsp;
              </Link>

              <Typography
                component="span"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: "inherit",
                  lineHeight: "inherit",
                }}
              >
                {text}
              </Typography>
            </Box>
          </Box>

          {/* RIGHT PANEL */}
          <Box
            sx={{
              width: "50%",
              backgroundColor: "#FFFFFF",
              p: "32px",
              gap: "24px",
              "@media (max-width: 1200px)": {
                width: "100%",
              },
              "@media (max-width: 780px)": {
                p: "16px",
              },
            }}
          >
            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <TextField
                label="Company Name"
                name="companyName"
                onChange={handleChange}
                value={formData.companyName}
                fullWidth
                variant="outlined"
                size="medium"
                required
                error={Boolean(errors.companyName)}
                helperText={errors.companyName}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: "#666",
                  },
                  "& .MuiInputLabel-asterisk": {
                    color: "#d32f2f",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "green",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "green",
                  },
                }}
              />

              <TextField
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                label="Full Name"
                fullWidth
                variant="outlined"
                size="medium"
                required
                error={Boolean(errors.fullName)}
                helperText={errors.fullName}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: "#666",
                  },
                  "& .MuiInputLabel-asterisk": {
                    color: "#d32f2f",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "green",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "green",
                  },
                }}
              />

              <TextField
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleChange}
                label="Job Title"
                fullWidth
                variant="outlined"
                size="medium"
                required
                error={Boolean(errors.jobTitle)}
                helperText={errors.jobTitle}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: "#666",
                  },
                  "& .MuiInputLabel-asterisk": {
                    color: "#d32f2f",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "green",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "green",
                  },
                }}
              />

              <TextField
                name="email"
                value={formData.email}
                onChange={handleChange}
                label="Email Address"
                fullWidth
                variant="outlined"
                size="medium"
                required
                error={Boolean(errors.email)}
                helperText={errors.email}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: "#666",
                  },
                  "& .MuiInputLabel-asterisk": {
                    color: "#d32f2f",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "green",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "green",
                  },
                }}
              />

              <TextField
                select
                name="country"
                value={formData.country}
                onChange={handleChange}
                label="Country"
                required
                fullWidth
                size="medium"
                error={Boolean(errors.country)}
                helperText={errors.country}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: "#666",
                  },
                  "& .MuiInputLabel-asterisk": {
                    color: "#d32f2f",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "green",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "green",
                  },
                }}
              >
              {Countries.map(country =>
                <MenuItem value={country}>{country}</MenuItem>
              )}
              </TextField>

              <TextField
                select
                name="state"
                value={formData.state}
                onChange={handleChange}
                label="State, if in the U.S."
                required
                error={Boolean(errors.state)}
                helperText={errors.state}
                fullWidth
                size="medium"                
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: "#666",
                  },
                  "& .MuiInputLabel-asterisk": {
                    color: "#d32f2f",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "green",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "green",
                  },
                }}
              >
              <MenuItem value="Not Applicable">Not Applicable</MenuItem>
              {States.map(state =>
                <MenuItem value={state}>{state}</MenuItem>
              )}                
              </TextField>                        

              <TextField
                name="referral"
                onChange={handleChange}
                value={formData.referral}
                label="How did you learn about DAM Journey?"
                fullWidth
                variant="outlined"
                size="medium"
                required
                error={Boolean(errors.referral)}
                helperText={errors.referral}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: "#666",
                  },
                  "& .MuiInputLabel-asterisk": {
                    color: "#d32f2f",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "green",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "green",
                  },
                }}
              />

              <TextField
                name="message"
                onChange={handleChange}
                value={formData.message}
                label="How can we help you?"
                required
                error={Boolean(errors.message)}
                helperText={errors.message}
                fullWidth
                multiline
                rows={5}
                variant="outlined"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: "#666",
                  },
                  "& .MuiInputLabel-asterisk": {
                    color: "#d32f2f",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "green",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "green",
                  },
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 1,
                }}
              >
                <Button
                  variant="contained"
                  type="submit"
                  sx={{
                    px: "32px",
                    py: "8px",
                    fontSize: "0,9375rem",
                    fontWeight: "medium",
                    borderRadius: "999px",
                    textTransform: "none",
                    background: "#C22A43",
                    // "&:hover": {
                    //   background:
                    //     "linear-gradient(90deg, #FF7FAC 0%, #5834BA 100%)",
                    // },
                  }}
                >
                  Submit
                </Button>
              </Box>

              <Typography
                sx={{
                  textAlign: "center",
                  color: "#666666",
                  fontSize: "1rem",
                  fontWeight: "regular",
                }}
              >
                By submitting this information, you agree
                to our{" "}
                <Link
                  href="/privacy-policy"
                  underline="hover"
                  sx={{
                    color: "#cc3344",
                  }}
                >
                  Privacy Policy
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Contact;
